import { Link } from "react-router-dom";
import { Star, MapPin, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { CATEGORY_LABELS, CATEGORY_COLORS, SUBCATEGORY_LABELS } from "../lib/api";

export default function PlaceCard({ place, onHover, onClick, index = 0, compact = false }) {
  const promoted = place.is_promoted;
  const catColor = CATEGORY_COLORS[place.category] || "#1A1A1A";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.4) }}
      onMouseEnter={() => onHover?.(place)}
      onClick={() => onClick?.(place)}
      data-testid={`place-card-${place.id}`}
      className={`group cursor-pointer rounded-3xl overflow-hidden bg-white border transition-all hover:-translate-y-1 hover:shadow-xl ${
        promoted ? "border-[#FF6B35]/30 shadow-md shadow-[#FF6B35]/10" : "border-black/5 shadow-sm"
      } ${compact ? "" : ""}`}
    >
      <div className={`relative overflow-hidden bg-[#FAF1E1] ${compact ? "h-44" : "h-48"}`}>
        {place.image_url ? (
          <img src={place.image_url} alt={place.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#1A1A1A]/30">
            <MapPin size={32} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        {promoted && (
          <div data-testid={`promoted-badge-${place.id}`}
            className="absolute top-3 right-3 bg-gradient-to-r from-[#FF6B35] to-[#E84A1F] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
            <Sparkles size={10} />
            Sponsorisé
          </div>
        )}
        <div
          className="absolute top-3 left-3 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
          style={{ background: `${catColor}E6` }}
        >
          {CATEGORY_LABELS[place.category]}
        </div>
        <div className="absolute bottom-3 right-3 backdrop-blur-md bg-white/90 px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 text-[#1A1A1A]">
          <Star size={11} className="fill-[#FF6B35] text-[#FF6B35]" />
          {place.rating?.toFixed(1)}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-lg text-[#1A1A1A] leading-tight">{place.name}</h3>
        <div className="flex items-center gap-1.5 mt-1.5 text-sm text-[#1A1A1A]/60">
          <MapPin size={12} />
          <span className="truncate">{place.city}</span>
          {place.subcategory && (
            <>
              <span>·</span>
              <span className="capitalize">{SUBCATEGORY_LABELS[place.subcategory] || place.subcategory.replace("_"," ")}</span>
            </>
          )}
        </div>
        {!compact && place.description && (
          <p className="text-sm text-[#1A1A1A]/60 mt-2 line-clamp-2">{place.description}</p>
        )}
        <Link to={`/place/${place.id}`} data-testid={`card-detail-${place.id}`}
          className="mt-3 inline-block text-sm font-bold text-[#FF6B35] hover:underline">
          En savoir plus →
        </Link>
      </div>
    </motion.div>
  );
}
